import React, { useRef, useState } from 'react';

import Link from 'next/link';
import ReCAPTCHA from 'react-google-recaptcha';
import { useToasts } from 'react-toast-notifications';

import Button from '@components/common/Button/Button';
import useTranslateLpc from '@components/common/hooks/useTranslateLpc';
import Text from '@components/common/Text/Text';
import AuthenticationPageContext from '@components/lpc/Authentication/AuthenticationPageContext';
import Auth from '@services/domain/Lpc/AuthService';
import { ROUTE } from '@services/http/Route';

import css from './EmailSentConfirmation.module.scss';

type EmailSentConfirmationProps = {
    fromDashboard?: boolean;
    fromDashboardEmail?: string;
};

const EmailSentConfirmation = ({ fromDashboard, fromDashboardEmail }: EmailSentConfirmationProps) => {
    const { t } = useTranslateLpc(['lpc-authentication', 'lpc-navbar', 'common']);
    const { addToast } = useToasts();

    const [isLoading, setIsLoading] = useState(false);
    const recaptchaRef = useRef<ReCAPTCHA>(null);

    const resendEmail = async (email) => {
        setIsLoading(true);
        const captcha = await recaptchaRef.current?.executeAsync();

        Auth.forgottenPassword({ email: email, captcha: captcha })
            .then(() =>
                addToast(t(`success.EMAIL_SEND`), {
                    appearance: 'success',
                    autoDismiss: true,
                }),
            )
            .finally(() => {
                setIsLoading(false);
                recaptchaRef.current?.reset();
            });
    };

    return (
        <AuthenticationPageContext.Consumer>
            {({ email }) => (
                <div
                    className={
                        fromDashboard ? `${css.custom_email_sent_confirmation} ` : ` ${css.email_sent_confirmation}`
                    }
                >
                    <ReCAPTCHA sitekey={process.env.RECAPTCHA_SITE_KEY} ref={recaptchaRef} size="invisible" />
                    <img src="/front-static/icons/display/checkmark-outline.svg" alt="checkmark" />
                    <h3>{t('email-sent-confirmation.title')}</h3>
                    <br />
                    <Text variant="body_02" color="ui-secondary">
                        {t('email-sent-confirmation.details')}{' '}
                    </Text>
                    <Text variant="body_01" color="txt-primary" customClass={css.email_sent_confirmation__email}>
                        {fromDashboard ? fromDashboardEmail : email}
                    </Text>
                    <Button
                        size="large"
                        variant="tertiary"
                        isLoading={isLoading}
                        isDisabled={isLoading}
                        margin={'32px 0 0 0'}
                        onClick={() => resendEmail(email)}
                    >
                        {t('email-sent-confirmation.no-email-received')}
                    </Button>

                    <Link href={fromDashboard ? ROUTE.LPC.HELP_CENTER.CONTACT : ROUTE.LPC.SIGN_IN}>
                        <Button
                            customClass={css.cta}
                            width={fromDashboard ? '225px' : '120px'}
                            type={'submit'}
                            size="large"
                            variant="primary"
                            isLoading={isLoading}
                            isDisabled={isLoading}
                        >
                            {fromDashboard ? t('email-sent-confirmation.contact') : t('sign-in')}
                        </Button>
                    </Link>
                </div>
            )}
        </AuthenticationPageContext.Consumer>
    );
};

export default EmailSentConfirmation;
